/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  TableFooter,
  TablePagination,
  IconButton,
} from "@material-ui/core";
import PropTypes from "prop-types";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import FirstPageIcon from "@material-ui/icons/FirstPage";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import LastPageIcon from "@material-ui/icons/LastPage";

import { useSelector } from "react-redux";
import { RootState } from "store/types";

import { skaleBlack, skaleWhite } from "config/UpduoColors";

const useStyles = makeStyles({
  table: {
    minWidth: 650,
  },
  tablecell: { fontFamily: "ObjectSans-Regular" },
});

const useStyles1 = makeStyles((theme) => ({
  root: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
  },
}));

function TablePaginationActions(props) {
  const classes = useStyles1();
  const theme = useTheme();
  const { count, page, rowsPerPage, onChangePage } = props;

  const handleFirstPageButtonClick = (event) => {
    onChangePage(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onChangePage(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onChangePage(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className={classes.root}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label={"first page"}
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label={"previous page"}
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label={"next page"}
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label={"last page"}
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onChangePage: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

export default function MetricTable() {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const metricList = useSelector((state: RootState) => state.metricList);

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, metricList.length - page * rowsPerPage);

  const _handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const _handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const classes = useStyles();

  const columnHeaderArray = [
    "Metric",
    "Created by File",
    "Date Created",
    "Description",
    "Unit",
  ];

  const columnHeaders = columnHeaderArray.map((item, itemIndex) => (
    <TableCell
      key={itemIndex + 1}
      style={{
        fontWeight: "bold",
        color: skaleBlack,
        fontFamily: "ObjectSans-Regular",
      }}
    >
      {item}
    </TableCell>
  ));

  return (
    <TableContainer component={Paper}>
      <Table
        className={classes.table}
        size={"small"}
        aria-label={"custom pagination table"}
      >
        <TableHead>
          <TableRow>{columnHeaders}</TableRow>
        </TableHead>
        <TableBody style={{ backgroundColor: skaleWhite }}>
          {metricList.length > 0
            ? metricList
                .filter((row) => row.is_active)
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item, itemIndex) => (
                  <TableRow key={itemIndex}>
                    <TableCell className={classes.tablecell}>
                      {item.name}
                    </TableCell>
                    <TableCell className={classes.tablecell}>
                      {item.metadata["file_name"]}
                    </TableCell>
                    <TableCell className={classes.tablecell}>
                      {item.metadata["uploaded_date"]}
                    </TableCell>
                    <TableCell className={classes.tablecell}>
                      {item.description}
                    </TableCell>
                    <TableCell className={classes.tablecell}>
                      {item.unit}
                    </TableCell>
                  </TableRow>
                ))
            : null}
          {emptyRows > 0 && (
            <TableRow style={{ height: 40 * emptyRows }}>
              <TableCell colSpan={6} />
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              component={"div"}
              rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
              colSpan={3}
              count={metricList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: { "aria-label": "rows per page" },
                native: true,
              }}
              onPageChange={_handlePageChange}
              onRowsPerPageChange={_handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
